import {
  getLeoUuid,
  LeoUUID,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
/* eslint-disable import/export */
export abstract class AssignProjectToAPIKeyRPC implements
  LeoRPC<AssignProjectToAPIKeyRPC.Request, AssignProjectToAPIKeyRPC.Response, AssignProjectToAPIKeyRPC.Errors.Errors>
{
  abstract execute(
    request: AssignProjectToAPIKeyRPC.Request
  ): Promise<LeoRPCResult<AssignProjectToAPIKeyRPC.Response, AssignProjectToAPIKeyRPC.Errors.Errors>>;
}

export namespace AssignProjectToAPIKeyRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    apiKeyId: string;
    projectIds: string[];
  }
  
  export class Request implements LeoRPCRequest {
    readonly isAssignProjectToAPIKeyRPCRequest: boolean = true;
  
    readonly apiKeyId: LeoUUID;
    readonly projectIds: LeoUUID[];
  
    static readonly constraints = {
      "projectIds": {
        "minSize": 1,
      }
    }
  
    constructor(
    apiKeyId: LeoUUID, 
    projectIds: LeoUUID[]
    ) {
      this.apiKeyId = apiKeyId;
      Request.validateProjectIds(projectIds);
      this.projectIds = projectIds;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const apiKeyId = getLeoUuid(dto, "apiKeyId", InvalidRequestApiKeyIdError);
      const projectIds = getList<string, LeoUUID>(
        dto,
        "projectIds",
        (e: string) => new LeoUUID(e),
        InvalidRequestProjectIdsError
      );
      return new Request(
        apiKeyId,
        projectIds
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          apiKeyId: this.apiKeyId.toDTO(),
          projectIds: this.projectIds.map((e: LeoUUID) => { return e.toDTO(); })
      };
      return dto;
    }
    private static validateProjectIds = (projectIds: LeoUUID[]): void => {
      if(projectIds.length < 1) {
        throw new InvalidRequestProjectIdsMinSizeError(
          1,
          `Attribute projectIds is too short. Size is ${projectIds.length}. Min size is 1.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `AssignProjectToAPIKeyRPC(apiKeyId='${this.apiKeyId}',projectIds='${this.projectIds}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `AssignProjectToAPIKeyRPC.Response()`;
  };

  export enum RPCError {
    InvalidApiKey = "INVALID_API_KEY",
    InvalidProjectId = "INVALID_PROJECT_ID"
  }

  export namespace Errors {
    
    export interface InvalidApiKeyDTO {
      code: string;
    }
    
    export class InvalidApiKey extends LeoRPCError<RPCError> {
      readonly isInvalidApiKey: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidApiKey;
        super(code);
      }
    
    
      toDTO(): InvalidApiKeyDTO {
        const dto: InvalidApiKeyDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidApiKey.prototype.toString = function toString(): string {
      return `AssignProjectToAPIKeyRPC.INVALID_API_KEY()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `AssignProjectToAPIKeyRPC.INVALID_PROJECT_ID()`;
    };
    

    export type Errors = InvalidApiKey | InvalidProjectId;
    export type ErrorsDTO = InvalidApiKeyDTO | InvalidProjectIdDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidApiKey:
          return new InvalidApiKey();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestApiKeyIdError extends AssignProjectToAPIKeyRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestApiKeyIdError";
  }
}

export class InvalidRequestProjectIdsError extends AssignProjectToAPIKeyRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdsError";
  }
}

export class InvalidRequestProjectIdsMinSizeError extends InvalidRequestProjectIdsError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdsMinSizeError";
  }
}


export class InvalidResponseInvalidApiKeyError extends AssignProjectToAPIKeyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidApiKeyError";
  }
}

export class InvalidResponseInvalidProjectIdError extends AssignProjectToAPIKeyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

